import { jsx as _jsx } from "react/jsx-runtime";
import { VolumeUpRounded, VolumeOffRounded, LockClockRounded, LockOpenRounded, LockRounded, } from '@mui/icons-material';
import { timeInPast_parts } from 'webui/utils/time';
import { ResponsiveIcon } from 'console/shared/components/icon/responsive';
import { useLegacyGuardControlState } from 'console/shared/hooks/device/legacy';
import { memo, useCallback, useMemo } from 'react';
import { GuardControlContent } from './types';
const icon_props = {
    fontSize: '1.1em',
};
export const LegacyGuardControlProvider = memo(function LegacyGuardControlProvider(props) {
    const { children, guard_controls_config } = props;
    const { onGuardControlClick } = guard_controls_config;
    const { guard_state__loading, last_guard_event, guard_state, guard_state__fail, action_fail_message, } = useLegacyGuardControlState();
    const guard_controls_state = useMemo(() => ({
        loading: guard_state__loading ?? false,
        fail: guard_state__fail,
        fail_message: action_fail_message,
        active: guard_state === 'enabled',
        turned_off: guard_state === 'disabled',
    }), [guard_state, guard_state__loading]);
    const { date, time } = timeInPast_parts(new Date((last_guard_event?.time ?? 0) * 1e3), {
        accusative: true,
    });
    const { loading, active, turned_off } = guard_controls_state;
    const onClick = useCallback(() => onGuardControlClick(turned_off), [turned_off]);
    const last_event_state = {
        turned_on: last_guard_event?.event === 'GuardOn',
        turned_off: last_guard_event?.event === 'GuardOff',
    };
    const last_event_label = useMemo(() => {
        if (last_event_state) {
            const { turned_off, turned_on } = last_event_state;
            if (turned_on) {
                return 'Поставлено на охрану';
            }
            if (turned_off) {
                return 'Снято с охраны';
            }
        }
        return null;
    }, [last_guard_event]);
    const { icon, label } = useMemo(() => {
        if (active) {
            return {
                icon: (_jsx(ResponsiveIcon, { Icon: LockRounded, color: "action", ...icon_props })),
                label: 'Под охраной',
            };
        }
        if (loading) {
            return {
                icon: (_jsx(ResponsiveIcon, { Icon: LockClockRounded, color: "action", ...icon_props })),
                label: 'Постановка',
            };
        }
        if (turned_off) {
            return {
                icon: (_jsx(ResponsiveIcon, { Icon: LockOpenRounded, color: "action", ...icon_props })),
                label: 'Снято с охраны',
            };
        }
        return { icon: null, label: null };
    }, [active, loading, turned_off]);
    const action_label = useMemo(() => {
        if (loading) {
            return 'Отправка команды...';
        }
        if (loading || active) {
            return 'Снять с охраны';
        }
        return 'Поставить на охрану';
    }, [loading, active]);
    const last_label = useMemo(() => (last_event_label ? `${last_event_label} ${date} в ${time}` : null), [last_event_label, date, time]);
    const guard_controls = useMemo(() => ({
        ...guard_controls_config,
        ...guard_controls_state,
        icon,
        label,
        action_label,
        last_label,
        onGuardControlClick: onClick,
    }), [
        guard_controls_config,
        guard_controls_state,
        icon,
        label,
        action_label,
        last_label,
        onClick,
    ]);
    return (_jsx(GuardControlContent.Provider, { value: guard_controls, children: children }));
});
export const LegacySirenControlProvider = memo(function LegacySirenControlProvider(props) {
    const { children, guard_controls_config } = props;
    const { siren__loading, siren, siren__fail, action_fail_message } = useLegacyGuardControlState();
    const { onGuardControlClick } = guard_controls_config;
    const siren_control_state = useMemo(() => ({
        loading: siren__loading ?? false,
        fail: siren__fail ?? false,
        fail_message: action_fail_message ?? '',
        active: siren ?? false,
        turned_off: !siren,
    }), [siren__loading, siren]);
    const { loading, active } = siren_control_state;
    const onClick = useCallback(() => onGuardControlClick(!active), [active]);
    const { icon, label } = useMemo(() => {
        if (active) {
            return {
                icon: (_jsx(ResponsiveIcon, { color: "action", Icon: VolumeUpRounded, fontSize: "1.1em" })),
                label: 'Сирена включена',
            };
        }
        return {
            icon: (_jsx(ResponsiveIcon, { color: "action", Icon: VolumeOffRounded, fontSize: "1.1em" })),
            label: 'Сирена выключена',
        };
    }, [active]);
    const action_label = useMemo(() => {
        if (loading) {
            return 'Отправка команды...';
        }
        if (active) {
            return 'Выключить сирену';
        }
        return 'Включить сирену';
    }, [loading, active]);
    const guard_controls = useMemo(() => ({
        ...guard_controls_config,
        ...siren_control_state,
        action_label,
        icon,
        label,
        onGuardControlClick: onClick,
    }), [guard_controls_config, siren_control_state, action_label, icon, label, onClick]);
    return (_jsx(GuardControlContent.Provider, { value: guard_controls, children: children }));
});
